import { supabase } from '@/integrations/supabase/client';
import { GeoLocationCache, CachedGeoLocation } from './GeoLocationCache';
import { debugLogger } from '@/utils/debugLogger';

export interface GoogleGeoLocationResult {
  success: boolean;
  data?: Omit<CachedGeoLocation, 'timestamp'>;
  error?: string;
  source: 'cache' | 'api' | 'error';
}

/**
 * Serviço para obter geolocalização via Google Geolocation API
 * Implementa rate limiting (60 chamadas/hora = 1 por minuto) usando cache persistente
 */
export class GoogleGeoLocationService {
  private static lastApiCallTime: number = 0;
  private static readonly MIN_API_INTERVAL_MS = 60000; // 1 minuto em ms

  /**
   * Verifica se pode fazer uma nova chamada à API
   */
  private static canCallApi(): boolean {
    const now = Date.now();
    const timeSinceLastCall = now - this.lastApiCallTime;
    return timeSinceLastCall >= this.MIN_API_INTERVAL_MS;
  }

  /**
   * Obtém localização do usuário
   * Prioriza cache válido, só chama API se necessário
   */
  static async getLocation(): Promise<GoogleGeoLocationResult> {
    try {
      // 1. Verificar cache primeiro
      const cached = GeoLocationCache.get();
      if (cached && GeoLocationCache.isValid()) {
        debugLogger.debug('geolocation', 'Using cached location:', {
          city: cached.city,
          ageMinutes: GeoLocationCache.getCacheAge()
        });

        return {
          success: true,
          data: {
            latitude: cached.latitude,
            longitude: cached.longitude,
            city: cached.city,
            state: cached.state,
            stateCode: cached.stateCode,
            country: cached.country,
            countryCode: cached.countryCode
          },
          source: 'cache'
        };
      }

      // 2. Cache expirado ou não existe - verificar rate limit
      if (!this.canCallApi()) {
        const waitTime = Math.ceil((this.MIN_API_INTERVAL_MS - (Date.now() - this.lastApiCallTime)) / 1000);
        const waitMinutes = Math.floor(waitTime / 60);
        const waitSeconds = waitTime % 60;
        
        debugLogger.warn('geolocation', 'Rate limit active. Next API call available in:', { seconds: waitTime });

        // ✅ Tentar buscar cache MESMO SE EXPIRADO (fallback de emergência)
        const expiredCache = GeoLocationCache.get(true); // allowExpired = true
        
        if (expiredCache) {
          const cacheAgeMinutes = Math.round((Date.now() - expiredCache.timestamp) / 60000);
          debugLogger.debug('geolocation', '✅ Using expired cache as fallback during rate limit', {
            city: expiredCache.city,
            cacheAgeMinutes
          });
          
          return {
            success: true,
            data: {
              latitude: expiredCache.latitude,
              longitude: expiredCache.longitude,
              city: expiredCache.city,
              state: expiredCache.state,
              stateCode: expiredCache.stateCode,
              country: expiredCache.country,
              countryCode: expiredCache.countryCode
            },
            source: 'cache'
          };
        }

        // ❌ Só retorna erro se NÃO HÁ CACHE ALGUM (primeira vez do usuário)
        debugLogger.error('geolocation', 'Rate limit AND no cache available (first-time user)');
        const errorMsg = waitMinutes > 0 
          ? `Rate limit ativo. Aguarde ${waitMinutes}min ${waitSeconds}s ou permita GPS.`
          : `Rate limit ativo. Aguarde ${waitSeconds}s ou permita GPS.`;
        
        return {
          success: false,
          error: errorMsg,
          source: 'error'
        };
      }

      // 3. Fazer chamada à API via edge function
      debugLogger.debug('geolocation', 'Calling Google Geolocation API');
      this.lastApiCallTime = Date.now();

      const { data, error } = await supabase.functions.invoke('google-geolocation', {
        body: {}
      });

      if (error) {
        debugLogger.error('geolocation', 'API call failed:', error);
        
        // ✅ Tentar usar cache expirado como fallback
        const expiredCache = GeoLocationCache.get(true); // allowExpired = true
        
        if (expiredCache) {
          debugLogger.debug('geolocation', '✅ Using expired cache as fallback after API error', {
            city: expiredCache.city
          });
          return {
            success: true,
            data: {
              latitude: expiredCache.latitude,
              longitude: expiredCache.longitude,
              city: expiredCache.city,
              state: expiredCache.state,
              stateCode: expiredCache.stateCode,
              country: expiredCache.country,
              countryCode: expiredCache.countryCode
            },
            source: 'cache'
          };
        }

        return {
          success: false,
          error: error.message || 'Failed to fetch location',
          source: 'error'
        };
      }

      // 4. Salvar no cache
      const locationData = {
        latitude: data.latitude,
        longitude: data.longitude,
        city: data.city,
        state: data.state || data.stateCode,
        stateCode: data.stateCode,
        country: data.country,
        countryCode: data.countryCode
      };

      GeoLocationCache.save(locationData);

      debugLogger.debug('geolocation', 'Location fetched from API:', {
        city: data.city,
        state: data.stateCode
      });

      return {
        success: true,
        data: locationData,
        source: 'api'
      };
    } catch (error: any) {
      debugLogger.error('geolocation', 'Exception:', error);
      
      // ✅ Última tentativa: usar cache expirado
      const expiredCache = GeoLocationCache.get(true); // allowExpired = true
      
      if (expiredCache) {
        debugLogger.debug('geolocation', '✅ Using expired cache after exception', {
          city: expiredCache.city
        });
        return {
          success: true,
          data: {
            latitude: expiredCache.latitude,
            longitude: expiredCache.longitude,
            city: expiredCache.city,
            state: expiredCache.state,
            stateCode: expiredCache.stateCode,
            country: expiredCache.country,
            countryCode: expiredCache.countryCode
          },
          source: 'cache'
        };
      }

      return {
        success: false,
        error: error.message || 'Unknown error',
        source: 'error'
      };
    }
  }

  /**
   * Força uma nova chamada à API ignorando cache e rate limit
   * Use apenas para refresh manual solicitado pelo usuário
   */
  static async forceRefresh(): Promise<GoogleGeoLocationResult> {
    debugLogger.debug('geolocation', 'Force refresh requested');
    GeoLocationCache.clear();
    this.lastApiCallTime = 0;
    return this.getLocation();
  }
}
